// Pricing helpers for the dashboard. Rates come from the generated backend
// catalog snapshot (modelCatalogFallback.json) — regenerate that file via
// qualitycompute/export_frontend_model_catalog.py when the backend catalog changes.
import fallbackModels from './modelCatalogFallback.json';

const PROVIDER_CATEGORY = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Google',
  meta: 'Meta',
  xai: 'X.AI',
  deepseek: 'DeepSeek',
  kimi: 'Kimi',
  glm: 'GLM',
};

function buildPricingTables(models) {
  const basePricing = {};
  const simpleNameMap = {};

  for (const model of models) {
    const id = model.id;
    const entry = {
      input: model.input,
      output: model.output,
      category: PROVIDER_CATEGORY[model.provider] || String(model.provider || 'Unknown'),
    };
    if (id === 'kimi-k3.1-pro' || id === 'kimi-k3.1-pro-highspeed') {
      for (const key of ['context_window', 'default_max_output_tokens', 'output_budget_field']) {
        entry[key] = model[key];
      }
    }
    if (model.image_token_ratio != null) entry.image_token_ratio = model.image_token_ratio;
    if (model.cached_input != null) entry.cached_input = model.cached_input;
    if (model.cache_writes != null) entry.cache_writes = model.cache_writes;
    if (model.cache_writes_5m != null) entry.cache_writes_5m = model.cache_writes_5m;
    if (model.cache_writes_1h != null) entry.cache_writes_1h = model.cache_writes_1h;
    if (model.cache_read_multiplier != null) entry.cache_read_multiplier = model.cache_read_multiplier;
    if (model.cache_creation_multiplier_5m != null) entry.cache_creation_multiplier_5m = model.cache_creation_multiplier_5m;
    if (model.cache_creation_multiplier_1h != null) entry.cache_creation_multiplier_1h = model.cache_creation_multiplier_1h;
    if (model.batch_rates != null) entry.batch_rates = model.batch_rates;
    if (model.cached_input_high != null) entry.cached_input_high = model.cached_input_high;
    if (model.prompt_threshold_tokens != null) entry.prompt_threshold_tokens = model.prompt_threshold_tokens;
    if (model.prompt_threshold_inclusive != null) entry.prompt_threshold_inclusive = model.prompt_threshold_inclusive;
    if (model.input_high != null) entry.input_high = model.input_high;
    if (model.output_high != null) entry.output_high = model.output_high;
    if (model.cache_hit != null) entry.cache_hit = model.cache_hit;
    if (model.pricing_transition != null) entry.pricing_transition = model.pricing_transition;
    if (model.service_tier_rates != null) entry.service_tier_rates = model.service_tier_rates;
    if (model.fast_rates != null) entry.fast_rates = model.fast_rates;
    if (model.flex_rates != null) entry.flex_rates = model.flex_rates;
    if (model.regional_multiplier != null) entry.regional_multiplier = model.regional_multiplier;
    if (model.prompt_cache_options != null) entry.prompt_cache_options = model.prompt_cache_options;
    if (model.max_input_tokens != null) entry.max_input_tokens = model.max_input_tokens;
    if (model.max_output_tokens != null) entry.max_output_tokens = model.max_output_tokens;
    if (model.input_modalities != null) entry.input_modalities = model.input_modalities;
    if (model.output_modalities != null) entry.output_modalities = model.output_modalities;
    if (model.reasoning_effort_options != null) entry.reasoning_effort_options = model.reasoning_effort_options;
    if (model.default_reasoning_effort != null) entry.default_reasoning_effort = model.default_reasoning_effort;

    basePricing[id] = entry;
    simpleNameMap[id] = id;
    for (const alias of model.alias_ids || []) {
      simpleNameMap[alias] = id;
    }
  }

  return { basePricing, simpleNameMap };
}

const { basePricing: BASE_PRICING_PER_MILLION_TOKENS, simpleNameMap: SIMPLE_NAME_TO_PRICING_KEY_MAP } =
  buildPricingTables(fallbackModels);

/**
 * Retrieve pricing info and Best-of-N for a given model ID.
 * @param {string} modelId   E.g. "gpt-4o-B5" or "claude-3-opus"
 * @returns {{ input:number, output:number, category:string, bestOfN:number }}
 */
function getPricingInfo(modelId) {
  const parts = String(modelId || '').split('-B');
  const baseName = parts[0];
  const bestOfN = parts.length > 1 ? parseInt(parts[1] || '8', 10) : 1;
  const pricingKey = SIMPLE_NAME_TO_PRICING_KEY_MAP[baseName] || baseName;
  const basePrices = BASE_PRICING_PER_MILLION_TOKENS[pricingKey];

  if (!basePrices) {
    console.warn(`Pricing not found for model "${modelId}" (mapped key: "${pricingKey}")`);
    return { input: 0, output: 0, category: 'Unknown', bestOfN };
  }

  return { ...basePrices, bestOfN };
}

export { getPricingInfo, BASE_PRICING_PER_MILLION_TOKENS, SIMPLE_NAME_TO_PRICING_KEY_MAP };
